import { IGeocodeResponse } from '../classes/interfaces';
import { PreRenderService } from './pre-render.service';
import {Observable, BehaviorSubject, merge, Subscription, of, timer} from 'rxjs';
import {Injectable, Inject} from '@angular/core';
import {UserService} from './user.service';
import {GoogleMapsService} from './google-maps.service';
import {Filters, PositionInfo} from '../classes/user-preferences';
import {SavedSearch} from '../classes/saved-search';
import {SearchType, ViewModes, SortBy, NotificationFrequency} from '../classes/enums';
import {User} from '../classes/user';
import {HasSubscriptions} from '../components/has-subscriptions';
import {Subject} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SearchFiltersParams} from '../classes/search-filters-params';
import { CommunicationService } from './communication.service';
import { Angulartics2 } from 'angulartics2';
import { transformAnalyticsTagForListingType } from '../shared/pipes/listing-type-analytics-tag.pipe';
import { map, debounceTime } from 'rxjs/operators';
import { IAppConfig } from '../app-config.interface';
import { APP_CONFIG, OARegions } from '../app-config.constants';
import { ApiConfigService } from './api-config/api-config.service';
import { ObjectResponse } from '../classes/object-response';
import { CookieService } from 'ngx-cookie';
import { AnalyticsService } from './analytics/analytics.service';
import { NOTIFICATION_CHECKBOX_VALUE } from '../classes/analytics-tags';
import { Listing } from '../classes/listing';
import { NeighbourhoodDetails, NeighbourhoodInterface } from '../neighbourhoods/classes/neighbourhood-details';
import { CacheService } from './cache.service';
import { Helper } from '../classes/helper';
import { BasicGeocodeResult } from '../classes/geocode';

export const THIRTY_DAYS_IN_MS = 2592000000;

@Injectable()
export class PreferencesService extends HasSubscriptions  {
  private _user: User = new User();
  // First value is true when the geolocation function is complete, second value is if it was successful at getting a location.
  doneGettingPhysicalLocation: BehaviorSubject<boolean[]> = new BehaviorSubject([false, false]);
  savedLocalSavedSearches: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _subjects: any;
  private static COOKIE_USER_LOCATION = 'remaxca-user-location';
  private static COOKIE_USER_IS_WC_REGION = 'wc-region';
  filtersChanged: Observable<Filters>;
  agentSearchFiltersChanged: Observable<SearchFiltersParams>;
  officeSearchFiltersChanged: Observable<SearchFiltersParams>;
  positionInfoChanged: Observable<PositionInfo>;
  favouriteListingsChanged: Observable<string[]>;
  favouriteAgentsChanged: Observable<string[]>;
  moreInfoRequestedChanged: Observable<string[]>;
  localeChanged: Observable<string>;
  viewedListingsChanged: Observable<string[]>;
  recentSearchesChanged: Observable<string[]>;
  savedSearchesChanged: Observable<SavedSearch[]>;
  nextListingsChanged: Observable<{listingId: string, detailUrl: string}[]>;
  viewModesChanged: Observable<ViewModes>;
  gallerySortingChanged: Observable<SortBy>;
  favouriteAgentUrlChanged: Observable<string>;

  constructor(private userService: UserService,
    private googleMapsService: GoogleMapsService,
    private communicationService: CommunicationService,
    private prerenderService: PreRenderService,
    private http: HttpClient,
    private angulartics2: Angulartics2,
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    private apiConfig: ApiConfigService,
    private _cache: CacheService,
    private cookieService: CookieService,
    private analyticsService: AnalyticsService
  ) {
    super();
    this._subjects = {
      filters: new BehaviorSubject(new Filters()),
      agentSearchFilters: new BehaviorSubject(new SearchFiltersParams()),
      officeSearchFilters: new BehaviorSubject(new SearchFiltersParams()),
      positionInfo: new BehaviorSubject<PositionInfo>(new PositionInfo()),
      moreInfoRequested: new BehaviorSubject<string[]>([]),
      locale: new BehaviorSubject<string>(null),
      viewedListings: new BehaviorSubject<string[]>([]),
      recentSearches: new BehaviorSubject<string[]>([]),
      savedSearches: new BehaviorSubject<SavedSearch[]>([]),
      nextListings: new BehaviorSubject<{listingId: string, detailUrl: string}[]>([]),
      viewModes: new BehaviorSubject<ViewModes>(ViewModes.GALLERY),
      gallerySorting: new BehaviorSubject<SortBy>(SortBy.NEWEST),
      favouriteListings: new BehaviorSubject<string[]>([]),
      favouriteAgents: new BehaviorSubject<string[]>([]),
      favouriteAgentDetailUrl: new BehaviorSubject<string>(null)
    };

    this.filtersChanged = this._subjects.filters.asObservable();
    this.agentSearchFiltersChanged = this._subjects.agentSearchFilters.asObservable();
    this.officeSearchFiltersChanged = this._subjects.officeSearchFilters.asObservable();
    this.positionInfoChanged = this._subjects.positionInfo.asObservable();
    this.favouriteListingsChanged = this._subjects.favouriteListings.asObservable();
    this.moreInfoRequestedChanged = this._subjects.moreInfoRequested.asObservable();
    this.localeChanged = this._subjects.locale.asObservable();
    this.viewedListingsChanged = this._subjects.viewedListings.asObservable();
    this.recentSearchesChanged = this._subjects.recentSearches.asObservable();
    this.savedSearchesChanged = this._subjects.savedSearches.asObservable();
    this.nextListingsChanged = this._subjects.nextListings.asObservable();
    this.viewModesChanged = this._subjects.viewModes.asObservable();
    this.gallerySortingChanged = this._subjects.gallerySorting.asObservable();
    this.favouriteAgentsChanged = this._subjects.favouriteAgents.asObservable();
    this.favouriteAgentUrlChanged = this._subjects.favouriteAgentDetailUrl.asObservable();

    this.addSub(this.userService.currentUser.subscribe(user => {
      if (user) {
        this._user = user;
        // Set up the preferences for the current user.
        this.setUserPreferencesFromServer();
      }
    }));

    this.addSub(
      merge(
        this.filtersChanged,
        this.agentSearchFiltersChanged,
        this.officeSearchFiltersChanged,
        this.favouriteListingsChanged,
        this.moreInfoRequestedChanged,
        this.localeChanged,
        this.viewedListingsChanged,
        this.savedSearchesChanged,
        this.nextListingsChanged,
        this.positionInfoChanged,
        this.viewModesChanged,
        this.gallerySortingChanged,
        this.favouriteAgentsChanged
      ).subscribe(() => {
        this._user.updatedByStorage = false;
    }));
  }

  public setLocale(locale: string) {
    this._user.preferences.locale = locale;
    // console.log('preferences.service: locale changed');
    this._subjects.locale.next(locale);
  }

  public setViewModes(viewModes: ViewModes) {
    this._user.preferences.viewMode = viewModes;
    // console.log('preferences.service: viewModes changed - ' + viewModes);

    if (this.prerenderService.isServerMode) {
      // Ensure view mode is never map on server
      return;
    }
    this._subjects.viewModes.next(viewModes);
  }

  public getGallerySorting(): SortBy {
    return this._user.preferences.gallerySorting;
  }

  public setGallerySorting(sortBy: SortBy) {
    this._user.preferences.gallerySorting = sortBy;
    this._subjects.gallerySorting.next(sortBy);
  }

  public updateFilters(filters: Filters) {
    this._user.preferences.filters = Filters.clone(filters);
    // console.log('preferences.service: filters changed');
    this._subjects.filters.next(filters);
  }

  public getFilters() {
    return this._user.preferences.filters;
  }

  public updateSearchFilters(searchType: SearchType, filters: SearchFiltersParams) {
    if (searchType === SearchType.AGENT) {
      this._user.preferences.agentSearchFilters = filters;
      this._subjects.agentSearchFilters.next(filters);
    } else {
      this._user.preferences.officeSearchFilters = filters;
      this._subjects.officeSearchFilters.next(filters);
    }

  }

  public saveUserPosition(positionInfo: PositionInfo) {
    const newPosition = PositionInfo.clone(positionInfo);
    this._user.preferences.positionInfo = newPosition;
    this.cookieService.putObject(PreferencesService.COOKIE_USER_LOCATION, newPosition);
    // console.log('preferences.service: positionInfo changed - ' + JSON.stringify(positionInfo));
    this._subjects.positionInfo.next(positionInfo);
  }

  public toggleFavouriteListing(listingId: string, analyticsTagSuffix: string) {
    const favourites = this._user.preferences.favouriteListings;
    const index = favourites.indexOf(listingId);

    let wasFavorite = true;
    // already exists, remove it
    if (index >= 0) favourites.splice(index, 1);
    else {
      favourites.push(listingId);
      wasFavorite = false;
    }

    if (!wasFavorite) {
      this.angulartics2.eventTrack.next({
        properties: {
          event: transformAnalyticsTagForListingType(analyticsTagSuffix, this.communicationService.listings.currentShownListingType.value)
        }
      });
    }
    // console.log('preferences.service: favourites changed');
    this._subjects.favouriteListings.next(favourites);
  }

  public toggleMoreInfoRequested(listingId: string) {
    const moreInfoRequested = this._user.preferences.moreInfoRequested;
    const index = moreInfoRequested.indexOf(listingId);
    // already exists, remove it
    if (index >= 0) moreInfoRequested.splice(index, 1);
    else moreInfoRequested.push(listingId);

    // console.log('preferences.service: moreInfoRequested changed');
    this._subjects.moreInfoRequested.next(moreInfoRequested);
  }

  public addViewedListing(listingId: string) {
    const viewedListings = this._user.preferences.viewedListings;
    // Only broadcast change if not already included
    if (viewedListings.filter((it) => it.split(';')[0] === listingId).length === 0) {
      viewedListings.push(listingId + ';' + Date.now());
      // console.log('preferences.service: viewedListings changed');
      this._subjects.viewedListings.next(viewedListings);
    }
  }

  public addRecentSearch(search: string) {
    const maxRecentSearches = 5;

    const recentSearches = this._user.preferences.recentSearches;
    if (recentSearches.some(s => s === search)) return;
    if (recentSearches.length >= maxRecentSearches) recentSearches.pop();
    recentSearches.unshift(search);
    // console.log('preferences.service: recentSearches changed');
    this._subjects.recentSearches.next(recentSearches);
  }



  public updateSavedSearch(savedSearch: SavedSearch): Promise<SavedSearch> {
    const index = this._user.preferences.savedSearches.findIndex((s) => s.name === savedSearch.name);
    return this.addNewSavedSearch(savedSearch.name, savedSearch.notificationsEnabled, index);
  }

  public updateNotificationOnSavedSearch(savedSearch: SavedSearch) {
    const index = this._user.preferences.savedSearches.findIndex((s) => s.name === savedSearch.name);
    const savedSearches = this._user.preferences.savedSearches;
    if (index >= 0) savedSearches[index].notificationsEnabled = savedSearch.notificationsEnabled;
    this.analyticsService.eventTrack(NOTIFICATION_CHECKBOX_VALUE, savedSearch.notificationsEnabled);
    this._subjects.savedSearches.next(savedSearches);
  }

  public deleteSavedSearch(s: SavedSearch) {
    const savedSearches = this._user.preferences.savedSearches;
    const index = savedSearches.findIndex((savedSearch) => s.name === savedSearch.name);
    savedSearches.splice(index, 1);
    this._subjects.savedSearches.next(savedSearches);
  }

  public deleteAllSavedSearches() {
    this._user.preferences.savedSearches = [];
    this._subjects.savedSearches.next([]);
  }

  public restoreSavedSearch(s: SavedSearch) {
    this.communicationService.setCurrentListingType(s.listingCategory);
    this.saveUserPosition(s.positionInfo);
    this.updateFilters(s.filters);
  }

  public doesSavedSearchExist(name: string): boolean {
    return this._user.preferences.savedSearches.some((s) => s.name === name);
  }

  public addNextListings(listings: Array<{listingId: string, detailUrl: string}>) {
    this._subjects.nextListings.next(listings);
  }

  private setIsWcRegion(addressComponents: {long_name: string, short_name:string, types: string[]}[]) {
    const date = new Date();
    const index = addressComponents.findIndex(g => OARegions.indexOf(g.short_name.toLowerCase()) !== -1);
    this._user.preferences.isWCRegion = index === -1;
    this.cookieService.put(PreferencesService.COOKIE_USER_IS_WC_REGION, this._user.preferences.isWCRegion.toString(), {expires: (1000 * 60 * 60 * 24 * 30).toString()})
    this.userService.saveUserPreferences(this._user.preferences);
  }

  /**
   * Geocode based on a search string, set zoom level from helper, get bounds, then set map to that
   * @param searchString the string being used for geocoding (typically from a search bar or URL path)
   * @param clientSide whether this request should use the client-side or server-side API. Use client-side for
   * things like user searches and use server-side for frequently repeated requests like city-province strings
   */
  public async centerMapThroughGeocode(searchString: string, clientSide: boolean): Promise<boolean> {
    /**
     * Once we geocode we do the following
     * 1) Show the Quebec modal if the searched location is in Quebec
     * 2) Update the search text string observable
     * 3) Update position observable and map if found
     */
    let results: BasicGeocodeResult;
    if (clientSide)
      results = await this.getClientSideGeocode(searchString);
    else
      results = await this.googleMapsService.performBasicGeocode(searchString);

    if (results && results.isQuebec) {
      this.communicationService.navigation.showSearch.next(false);
      this.communicationService.notifications.showQuebecModal.next(true);
      return false;
    }

    this.communicationService.searching.activeSearchText.next(searchString);

    if (results) {
      // update position info using backcalculated bounds
      const latLng = results.location;
      const zoomLevel = results.zoom;
      const bounds = this.googleMapsService.getBoundsForLocation(latLng, zoomLevel);
      this.setPosition(latLng.lat, latLng.lng, zoomLevel, bounds.south, bounds.west, bounds.north, bounds.east);

      // since we set the position explicitly, don't center on user
      this.communicationService.searching.shouldCenterAtUserLocation.next(false);

      // update the map to this location
      this.communicationService.searching.updateMap.next({ location: latLng, zoom: zoomLevel, isSearchForLocation: true });

      return true; // indicate successful geocode
    }
    return false;
  }

  // converts the client side maps geocode to the same type as the basic server side one
  private async getClientSideGeocode(searchString: string): Promise<BasicGeocodeResult> {
    const results = await this.googleMapsService.performGeocode(searchString);

    if (results) {
      const isQuebec = results.some((r) => r.address_components && r.address_components.some((c) => 'quebec' === c.long_name.toLowerCase()));
      const geometry = results[0].geometry;
      const latLng = geometry.location.toJSON();
      const zoomLevel = Helper.getZoomLevel(results[0].types);

      const basicGeocodeResult: BasicGeocodeResult = {
        location: latLng,
        isQuebec: isQuebec,
        zoom: zoomLevel
      }

      return new Promise<BasicGeocodeResult>((resolve) => resolve(basicGeocodeResult));
    }
    return new Promise<BasicGeocodeResult>((resolve) => resolve(null));
  }

  /**
   * Get the user's current Geolocation and set the map marker to that
   * @param shouldSkipGettingLocation whether or not we should actually retrieve their location, we would specifically skip this in the situation they land
   * on the a page that redirects the map to a specific location which has a higher precedence then the user's location such as /on/cornwall-real-estate
   * @param forceCenterLocation whether we want to explciitly call the navigator to center our location (bypass the cookie check)
   */
  public centerMapAtCurrentLocation(shouldSkipGettingLocation: boolean = false, forceCenterLocation = false) {
    if (shouldSkipGettingLocation || this.prerenderService.isServerMode) {
      this.doneGettingPhysicalLocation.next([true, true]);
      return;
    }

    // We don't want to recall the navigator if we've already fetched their location before, which will be stored in the cookie
    // The exception to this is that they manually choose to center their location via an action button on the map
    const userPosition = this.cookieService.getObject(PreferencesService.COOKIE_USER_LOCATION);
    if (userPosition && userPosition['physicalLocation'] && !forceCenterLocation) {
      const position: PositionInfo = userPosition as PositionInfo;
      const wcRegion = this.cookieService.get(PreferencesService.COOKIE_USER_IS_WC_REGION);
      if (!wcRegion) {
        this._handleGeoLocationByIp();
      }
      this.setPosition(position.physicalLocation.lat, position.physicalLocation.lng, position.zoomLevel);
      this.communicationService.searching.updateMap.next({ location: { lat: position.physicalLocation.lat, lng: position.physicalLocation.lng }, zoom: position.zoomLevel, isPhysical: true });
      this.doneGettingPhysicalLocation.next([true, true]);
    }
    // console.log('preferences.service: centerMapAtCurrentLocation');
    else if (typeof navigator !== 'undefined' && navigator.geolocation) {
      // Let the subscribers know we are in the process of getting a location.
      this.doneGettingPhysicalLocation.next([false, false]);

      navigator.geolocation.getCurrentPosition(
      (position) => {
        // Update the placeholder for the searchbar with the area they're in.
        this.googleMapsService.performReverseGeocode({lat: position.coords.latitude, lng: position.coords.longitude}).then((results) => {
          this.setIsWcRegion(results[0].address_components)
          this.communicationService.searching.activeSearchText.next(results[0].formatted_address);
        });
        this.setPosition(position.coords.latitude, position.coords.longitude, 17);
        // Let the subscribers know we successfully got a location.
        this.doneGettingPhysicalLocation.next([true, true]);
        this.communicationService.searching.updateMap.next({ location: { lat: position.coords.latitude, lng: position.coords.longitude }, zoom: 17, isPhysical: true });
      },
      (error) => {
        // Errors will fall here for either timeout, location not available or user denied geolocation.
        // For whatever reason we failed, so try and geolocate by IP.
        this._handleGeoLocationByIp();
      }, { timeout: 10000, enableHighAccuracy: true, maximumAge: 60000 });
    }
    else {
      // Browser does not support geolocation, try and locate by ip
      this._handleGeoLocationByIp();
    }
  }

  private _handleGeoLocationByIp() {
    this._geolocateByIp()
    .subscribe(
      success => {
        if (success) {
          // Let the subscribers know we successfully got a location.
          this.doneGettingPhysicalLocation.next([true, true]);
        } else {
          // Let the subscribers know we couldn't get a location.
          this.doneGettingPhysicalLocation.next([true, false]);
        }
      },
      err => {
        this.doneGettingPhysicalLocation.next([true, false]);
      }
    );
  }

  /**
   * Attempt to call freegeoip.net to get an IP based location.
   * Their service is based on the free database provided by MaxMind, but is rate limited to 10,000 requests per hour.
   * We only run this on a failed geolocation or if the browser doesn't support it, so we shouldn't run into the rate limit.
   * @private
   * @returns
   * @memberof PreferencesService
   */
  private _geolocateByIp() {
    return this.http.get<ObjectResponse<IGeocodeResponse>>(this.apiConfig.getServerRoot(this.appConfig.urls.geo.base) + this.appConfig.urls.geo.base)
    .pipe(map(
      data => {
        if (!data || !data.result) {
          return false;
        }
        let isWCRegion = false;
        if (OARegions.indexOf(data.result.region_code.toLowerCase()) != -1) {
          isWCRegion = false;
        } else {
          isWCRegion = true;
        }
        this.cookieService.put(PreferencesService.COOKIE_USER_IS_WC_REGION, isWCRegion.toString(), {expires: (1000 * 60 * 60 * 24 * 30).toString()})
        // Lower zoom since IP location is not very accurate.
        this.userService.saveUserPreferences(this._user.preferences);
        this.setPosition(data.result.latitude, data.result.longitude, 12);
        this.communicationService.searching.updateMap.next({ location: PositionInfo.getCenter(this._user.preferences.positionInfo), zoom: this._user.preferences.positionInfo.zoomLevel, isPhysical: true });
        return true;
      },
      error => {
        return false;
      }
    ));
  }

  public getPosition() {
    return this._user.preferences.positionInfo;
  }

  public getOfficeSearchFilters() {
    return this._subjects.officeSearchFilters.value;
  }

  public getIsWcRegion() {
    const wcRegion = this.cookieService.get(PreferencesService.COOKIE_USER_IS_WC_REGION);
    return wcRegion && wcRegion === 'false' ? false : true;
  }

  /**
   * Takes in position info and updates the user preferences with the new position.
   * This will invoke all subscribers to the positionInfo to get an update with the new position.
   * @memberof PreferencesService
   */
  public setPosition(lat: number, lng: number, zoomLevel: number, south?: number, west?: number, north?: number, east?: number) {
    const newPosition = this._user.preferences.positionInfo;
    // By setting centerLatLng, the map will move to be centered on us.
    newPosition.centerLatLng = {
      lat: lat,
      lng: lng
    };
    newPosition.defaultPosition = false;
    // We set the physical location to keep track of us even if the map moves later on.
    // TODO: is this being used correctly it is being set whenever this is called?
    newPosition.physicalLocation = {
      lat: lat,
      lng: lng
    };
    newPosition.zoomLevel = zoomLevel;

    if (south) newPosition.south = south;
    if (west) newPosition.west = west;
    if (north) newPosition.north = north;
    if (east) newPosition.east = east;

    this.saveUserPosition(newPosition);
    this.cookieService.putObject(PreferencesService.COOKIE_USER_LOCATION, newPosition);
  }

  private getNeighbourhoodData(id: string): Observable<NeighbourhoodDetails> {
    const params = new HttpParams();
    const base = this.apiConfig.getServerRoot(this.appConfig.urls.demographics.neighbourhoodId);
    const route = `${this.appConfig.urls.demographics.neighbourhoodId}/${id}`;
    const hashKey = this._cache.getRequestHash(route, params);
    const hashValue = this._cache.get<NeighbourhoodInterface>(hashKey);
    if (hashValue) {
      return of(new NeighbourhoodDetails(hashValue));
    }
    return this.http.get<ObjectResponse<NeighbourhoodInterface>>(base + route, { withCredentials: false }).pipe(
      map(data => new NeighbourhoodDetails(data.result)),
    )
  }

  public addNewSavedSearch(name: string, notification: boolean, index: number = -1, listing?: Listing): Promise<SavedSearch> {
    const neighbourhoodRegex = /-real-estate-guide-([\w\d_]+)$/;
    const newSearch = new SavedSearch();

    newSearch.name = name;
    newSearch.notificationsEnabled = notification;
    newSearch.notificationsFrequency = NotificationFrequency.DAILY;

    if (listing != null) {
      return new Promise((resolve, reject) => {
        const matches = listing.neighbourhood ? neighbourhoodRegex.exec(listing.neighbourhood.detailUrl) : null;
        // try to fetch neighbourhood data from listing if neighbourhood data exists
        // there are listings without neighbourhood data
        const neighbourhoodSearchSubscription = matches && matches.length !== 0 ? this.getNeighbourhoodData(matches[1]) : of(null);

        neighbourhoodSearchSubscription.subscribe(neighbourhoodDetails => {
          newSearch.filters = Filters.clone(this._user.preferences.filters);
          newSearch.positionInfo = new PositionInfo();
          newSearch.listingCategory = this.communicationService.listings.currentShownListingType.value;

          // if neighbourhoodDetails exists, set bounds to neighbouhood, else set zoom level to 18
          if (neighbourhoodDetails) {
            newSearch.positionInfo.setProperties(listing.lat, listing.lng, neighbourhoodDetails.bounds, listing.lat, listing.lng);
          } else {
            newSearch.positionInfo.centerLatLng = {lat: listing.lat, lng: listing.lng}
            newSearch.positionInfo.physicalLocation = {lat: listing.lat, lng: listing.lng}
            newSearch.positionInfo.zoomLevel = 18;
          }

          this.googleMapsService.performReverseGeocode(newSearch.positionInfo.centerLatLng).then(r => {
            if (!r) reject();
            else {
              const result = this.googleMapsService.getLocationTypeFromZoomLevel(newSearch.positionInfo.zoomLevel, r);
              if (result.types.indexOf('establishment') >= 0) newSearch.nearby = result.address_components[0].long_name;
              else newSearch.nearby = result.formatted_address;
              const searches = this._user.preferences.savedSearches;
              if (index >= 0) searches[index] = newSearch;
              else searches.push(newSearch);
              this._subjects.savedSearches.next(searches);
              resolve(newSearch);
            }
          });
        });
      });
    } else {
      newSearch.filters = Filters.clone(this._user.preferences.filters);
      newSearch.positionInfo = PositionInfo.clone(this._user.preferences.positionInfo);
      newSearch.listingCategory = this.communicationService.listings.currentShownListingType.value;
      return new Promise((resolve, reject) => {

        this.googleMapsService.performReverseGeocode(newSearch.positionInfo.centerLatLng).then(r => {
          if (!r) reject();
          else {
            const result = this.googleMapsService.getLocationTypeFromZoomLevel(newSearch.positionInfo.zoomLevel, r);
            if (result.types.indexOf('establishment') >= 0) newSearch.nearby = result.address_components[0].long_name;
            else newSearch.nearby = result.formatted_address;
            const searches = this._user.preferences.savedSearches;
            if (index >= 0) searches[index] = newSearch;
            else searches.push(newSearch);
            this._subjects.savedSearches.next(searches);
            resolve(newSearch);
          }
        });
      });
    }
  };

  // TODO: IS there a better way to do this
  public setUserPreferencesFromServer() {
    // console.log('preferences.service: setting preferences from server');
    const s = this._user.preferences;
    // Need to do follow this to update observables properly
    this.setLocale(s.locale);
      // If its Google Bot we want to render in gallery mode because it'll take too long for the google bot to see the map and render the listings before indexing
    this.setViewModes(this.prerenderService.isBrowserMode && !this.userService.isGoogleBot() ? s.viewMode : ViewModes.GALLERY);
    this.setGallerySorting(s.gallerySorting);
    this.updateFilters(s.filters);
    this.updateSearchFilters(SearchType.AGENT, s.agentSearchFilters);
    this.updateSearchFilters(SearchType.OFFICE, s.officeSearchFilters);
    if (s.positionInfo) {
      this.communicationService.searching.updateMap.next({ location: PositionInfo.getCenter(s.positionInfo), zoom: s.positionInfo.zoomLevel });
    }
    this.saveUserPosition(s.positionInfo);
    this._addAllFavouriteListings(s.favouriteListings || []);
    this._addAllRecentSearches(s.recentSearches || []);
    this._addAllSavedSearches(s.savedSearches || []);
    this._addAllViewedListings(s.viewedListings || []);
    this._addFavouriteAgent({favourites: (s.favouriteAgents || []), detailUrl: s.favouriteAgentDetailUrl});
  }

  private _addAllFavouriteListings(favourites: string[]) {
    this._user.preferences.favouriteListings = favourites;
    this._subjects.favouriteListings.next(favourites);
  }

  private _addFavouriteAgent(data: {favourites: string[], detailUrl?: string}) {
    this._user.preferences.favouriteAgents = data.favourites;
    this._user.preferences.favouriteAgentDetailUrl = data.detailUrl;
    this._subjects.favouriteAgents.next(data.favourites);
    this._subjects.favouriteAgentDetailUrl.next(data.detailUrl);
  }

  private _addAllSavedSearches(savedSearches: SavedSearch[]) {
   const localSavedSearches: SavedSearch[] = this.userService.getLocalSavedSearches();
    if (localSavedSearches && localSavedSearches.length !== 0 && this._user.email) {
      this.addLocalSavedSearches(savedSearches, localSavedSearches);
    }
    else {
      this._user.preferences.savedSearches = savedSearches;
      this._subjects.savedSearches.next(savedSearches);
    }
  }

  private addLocalSavedSearches(savedSearches: SavedSearch[], localSS: SavedSearch[]) {
    savedSearches.forEach((savedSearch, ssIndex) => {
      const index = localSS.findIndex((s) => s.name === savedSearch.name);
      if (index >= 0) {
        savedSearches[ssIndex] = localSS[index];
        localSS =  localSS.filter((ss) => ss.name !== savedSearch.name)
      }
    });
    savedSearches = savedSearches.concat(localSS);
    this._user.preferences.savedSearches = savedSearches;
    // this.userService.saveUserPreferences(this._user.preferences);
    this.savedLocalSavedSearches.next(true);
    this._subjects.savedSearches.next(savedSearches);
  }

  private _addAllRecentSearches(recentSearches: string[]) {
    this._user.preferences.recentSearches = recentSearches;
    this._subjects.recentSearches.next(recentSearches);
  }

  /**
   * this method is called when the user first loads the app
   * we'll go through all the viewed listings and purge the ones that are older than 30 days
   * @param {string[]} viewedListings
   * @private
   */
  private _addAllViewedListings(viewedListings: string[]) {
    viewedListings = viewedListings.filter((item) => {
      const viewedDate = Number(item.split(';')[1]);
      return viewedDate >= Date.now() - THIRTY_DAYS_IN_MS;
    });
    this._user.preferences.viewedListings = viewedListings;
    this._subjects.viewedListings.next(viewedListings);
  }
}
